import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, map, shareReplay, switchMap } from 'rxjs';
import type { GetFeedResponse, GetFeedQueryParameters, ContentEntry, DocsPage } from '@kempoweroyj/ngx-content-global-api-client';
import { InternalTokenService } from './internal-token.service';

export interface ContentCdnConfig {
    url: string;
    deliveryToken: string;
    stage: string;
}

export const CONTENT_CDN_CONFIG = new InjectionToken<ContentCdnConfig>('CONTENT_CDN_CONFIG');

/** Optional override token for sysAdmin users to see internal entries */
export const INTERNAL_DELIVERY_TOKEN = new InjectionToken<{ token: string | null }>('INTERNAL_DELIVERY_TOKEN');

export interface DocsNavItem {
    slug: string;
    title: string;
    type?: string;
    parentSlug?: string;
    orderIndex?: number;
    tags?: string[];
    route?: string;
}

/**
 * Service for reading content via CloudFront CDN with delivery token authentication.
 * Used by ChargeEye consumer app for public content reads (feeds, entries, docs).
 *
 * All requests wait for the internal token fetch to complete before firing,
 * ensuring sysAdmin users always see internal entries.
 */
@Injectable()
export class ContentCdnService {
    readonly #http = inject(HttpClient);
    readonly #config = inject(CONTENT_CDN_CONFIG);
    readonly #internalToken = inject(InternalTokenService, { optional: true });
    readonly #docsCache = new Map<string, Observable<DocsNavItem[]>>();

    /** Wait for internal token fetch, then build headers with the best available token. */
    #headers$(): Observable<HttpHeaders> {
        const ready = this.#internalToken?.ready ?? Promise.resolve(null);
        return from(ready).pipe(
            map(() => {
                const token = this.#internalToken?.token ?? this.#config.deliveryToken;
                return new HttpHeaders({ 'X-Delivery-Token': token });
            }),
        );
    }

    getFeed(stage: string, opts: GetFeedQueryParameters = {}): Observable<GetFeedResponse> {
        const params: Record<string, string> = {};
        if (opts.limit != null) params['limit'] = opts.limit;
        if (opts.cursor) params['cursor'] = opts.cursor;
        if (opts.tag) params['tag'] = opts.tag;
        if (opts.type) params['type'] = opts.type;
        if (opts.area) params['area'] = opts.area;

        return this.#headers$().pipe(
            switchMap((headers) =>
                this.#http.get<GetFeedResponse>(
                    `${this.#config.url}/api/content/cdn/feeds/${stage}`,
                    { headers, params },
                ),
            ),
        );
    }

    getEntry(id: string): Observable<ContentEntry & { detailsHtml?: string }> {
        return this.#headers$().pipe(
            switchMap((headers) =>
                this.#http.get<{ entry: ContentEntry & { detailsHtml?: string } }>(
                    `${this.#config.url}/api/content/cdn/entries/${id}`,
                    { headers },
                ),
            ),
            map((r) => r.entry),
        );
    }

    getDoc(slug: string): Observable<DocsPage & { bodyHtml?: string }> {
        return this.#headers$().pipe(
            switchMap((headers) =>
                this.#http.get<{ doc: DocsPage & { bodyHtml?: string } }>(
                    `${this.#config.url}/api/content/cdn/docs/${slug}`,
                    { headers },
                ),
            ),
            map((r) => r.doc),
        );
    }

    listDocs(type?: string): Observable<DocsNavItem[]> {
        const key = type ?? '__all__';
        let cached = this.#docsCache.get(key);
        if (!cached) {
            const params: Record<string, string> = {};
            if (type) params['type'] = type;
            cached = this.#headers$().pipe(
                switchMap((headers) =>
                    this.#http.get<{ docs: DocsNavItem[] }>(
                        `${this.#config.url}/api/content/cdn/docs`,
                        { headers, params },
                    ),
                ),
                map((r) => r.docs),
                shareReplay(1),
            );
            this.#docsCache.set(key, cached);
        }
        return cached;
    }
}
